/**
 * The loopback signing proxy on a kiosk's own Pi.
 *
 * The backend wants every kiosk call (`terminal-*`, `coin-*`, `wallet-redeem`) signed with the station's
 * Ed25519 key, which a browser cannot hold. The Pi's payment daemon runs a proxy on 127.0.0.1 that signs
 * and forwards instead, and provisioning appends its address to the kiosk URL as `?stationProxy=…`.
 *
 * The URL is authoritative on every load: a value sets the proxy, an EMPTY value clears it. Provisioning
 * always sends the parameter, so switching a station back to direct calls is a manifest edit rather than a
 * browser profile wipe.
 */
const STORAGE_KEY = 'jjames-stationProxy';

// Loopback only. A link that could point this at any other host would send the terminal's calls — and
// the parker's plate — wherever the link said.
const LOOPBACK_PROXY = /^http:\/\/127\.0\.0\.1:\d{2,5}$/;

/** Applies `stationProxy` from the page URL to storage. Returns true if the URL carried the parameter. */
export function captureStationProxy(params: URLSearchParams): boolean {
  if (!params.has('stationProxy')) {
    return false;
  }
  const value = (params.get('stationProxy') ?? '').trim().replace(/\/+$/, '');
  if (LOOPBACK_PROXY.test(value)) {
    localStorage.setItem(STORAGE_KEY, value);
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
  return true;
}

/** The proxy base URL for this station, or null when the kiosk talks to the backend directly. */
export function readStationProxy(): string | null {
  const value = localStorage.getItem(STORAGE_KEY);
  return value && LOOPBACK_PROXY.test(value) ? value : null;
}

/**
 * A fresh station-signed token for the payment-terminal hub, from the proxy on this Pi.
 *
 * The hub refuses an unsigned connection for an enforced terminal, and accepts a token for 300 s only, so
 * SignalR asks for one on every (re)connect. Plain `fetch`, not the generated client: this is the proxy's
 * own route on loopback, not a backend endpoint in the OpenAPI spec.
 */
export async function fetchStationHubToken(proxy: string): Promise<string> {
  const response = await fetch(`${proxy}/station/hub-token`);
  if (!response.ok) {
    throw new Error(`Station proxy refused a hub token (${response.status}).`);
  }
  const body = (await response.json()) as { token?: string };
  if (!body.token) {
    throw new Error('Station proxy returned no hub token.');
  }
  return body.token;
}
